import { ObservableSet } from 'mobx';
import type { AccountKey, MailboxInfo } from 'owa-client-types';
import { getAccountKeyForMailboxInfo } from 'owa-client-types';
import { createStore } from 'owa-satcheljs';
import type { NotificationsFilterValues } from '../components/NotificationsFilter';
import type { ActivityFeedItem } from '../service/ActivityFeedItem';
import { FeedActionType, type FeedAction } from './feedAction';
import type { TraceErrorObject } from 'owa-analytics-types';

export interface ActivityFeedStore {
    feedAction: FeedAction;
    isActivityFeedOpen: boolean;
    isActivityFeedSettingOpen: boolean;
    isOpx: boolean;
    accountItems: Map<AccountKey, ActivityFeedAccountStore>; // Account key to store of feed items for the account
    focusedItemId: string;
    notificationsDisabled: boolean;
    cardFilters?: ObservableSet<NotificationsFilterValues>;
}

export interface ActivityFeedAccountStore {
    firstFetchComplete: boolean;
    currentlyRenderedItemIds: ObservableSet<string>; // Filtered list of feed item IDs that are visible
    items: Map<string, ActivityFeedItem>; // Full feed item info
    unseenActivityCount: number;
    loadSucceeded: boolean;
    batchCount: number;
    lastSeenTimestamp: string | null;
    canShowNoNewNotificationsUI: boolean;
}

export const getActivityFeedStore: () => ActivityFeedStore = createStore<ActivityFeedStore>(
    'activityFeedStore',
    {
        feedAction: {
            promptFeedAction: false,
            type: FeedActionType.None,
            feedActionCallback: () => {},
        },
        isActivityFeedOpen: false,
        isActivityFeedSettingOpen: false,
        isOpx: false,
        accountItems: new Map<AccountKey, ActivityFeedAccountStore>(),
        focusedItemId: '',
        notificationsDisabled: false,
        cardFilters: undefined,
    }
);

export const getDefaultActivityFeedAccountStore = (): ActivityFeedAccountStore => {
    return {
        firstFetchComplete: false,
        currentlyRenderedItemIds: new ObservableSet<string>(),
        items: new Map<string, ActivityFeedItem>(),
        unseenActivityCount: 0,
        loadSucceeded: false,
        batchCount: 1,
        lastSeenTimestamp: null,
        canShowNoNewNotificationsUI: true,
    };
};

export const createFeedAccountStore = (accountStoreId: AccountKey): ActivityFeedAccountStore => {
    const accountStoresMap = getActivityFeedStore().accountItems;

    // Check if store already exists
    let accountStore = accountStoresMap.get(accountStoreId);

    if (!accountStore) {
        // Create new account store directly as a plain object (not a Satchel store)
        accountStore = getDefaultActivityFeedAccountStore();
        accountStoresMap.set(accountStoreId, accountStore);
    }

    return accountStore;
};

export const getActivityFeedAccountStore = (mailboxInfo: MailboxInfo): ActivityFeedAccountStore => {
    const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);
    const accountStore = getActivityFeedStore().accountItems.get(accountKey);
    if (!accountStore) {
        const err: TraceErrorObject = new Error(
            'getActivityFeedAccountStore is called for an account where initializeNotificationsPaneForAccount has not been called.'
        );
        err.additionalInfo = { diagnosticMessage: mailboxInfo.mailboxSmtpAddress };
        throw err;
    }
    return accountStore;
};

export const getActivityFeedAccountStores = (
    mailboxInfos: MailboxInfo[]
): {
    accountStore: ActivityFeedAccountStore;
    accountKey: AccountKey;
}[] => {
    return mailboxInfos.map(mailboxInfo => {
        const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);
        const accountStore = getActivityFeedStore().accountItems.get(accountKey);
        if (!accountStore) {
            const err: TraceErrorObject = new Error(
                'getActivityFeedAccountStores is called for an account where initializeNotificationsPaneForAccount has not been called.'
            );
            err.additionalInfo = { diagnosticMessage: mailboxInfo.mailboxSmtpAddress };
            throw err;
        }
        return { accountStore, accountKey };
    });
};

export const getActivityFeedItem = (
    itemId: string,
    mailboxInfo: MailboxInfo
): ActivityFeedItem | undefined => {
    const accountStore = getActivityFeedAccountStore(mailboxInfo);
    const feedItem = accountStore.items.get(itemId);
    return feedItem;
};

interface ActivityFeedSelectedAccountsStore {
    mailboxes: MailboxInfo[];
    setMailboxes: (mailboxes: MailboxInfo[]) => void;
    addMailbox: (mailbox: MailboxInfo) => void;
    removeMailbox: (mailbox: MailboxInfo) => void;
}

export const getActivityFeedSelectedAccountsStore: () => ActivityFeedSelectedAccountsStore =
    createStore<ActivityFeedSelectedAccountsStore>('activityFeedSelectedAccountsStore', {
        mailboxes: [],
        setMailboxes(mailboxes: MailboxInfo[]) {
            this.mailboxes = mailboxes;
        },
        addMailbox(mailbox: MailboxInfo) {
            // Only add if not already present
            if (
                !this.mailboxes.find(
                    m => getAccountKeyForMailboxInfo(m) === getAccountKeyForMailboxInfo(mailbox)
                )
            ) {
                this.mailboxes.push(mailbox);
            }
        },
        removeMailbox(mailbox: MailboxInfo) {
            this.mailboxes = this.mailboxes.filter(
                m => getAccountKeyForMailboxInfo(m) !== getAccountKeyForMailboxInfo(mailbox)
            );
        },
    });
